import { useReveal } from '../hooks/useReveal';
import { BrowserWindow } from './shared/BrowserWindow';
import { CommentMarker, hueFor } from './comment/CommentMarker';
import './Samples.scss';

/**
 * Three offset touts demonstrating PageLoop running on top of three
 * wildly different web genres:
 *   1. Documentation site (clean reference look)
 *   2. SaaS web app (dense, engineered)
 *   3. Boutique art / fashion site (serif, editorial, cream)
 *
 * Each row alternates: image-right + text-left → image-left + text-right →
 * image-right + text-left. Fades in as it scrolls into the viewport.
 *
 * The mini windows are pure HTML/SCSS mockups, deliberately small and
 * stylized so the page reads "this product works anywhere", not "here
 * are three live demos to read."
 */
export function Samples() {
	return (
		<section class="m-samples" id="samples">
			<header class="m-samples__head">
				<span class="m-samples__kicker">it works anywhere</span>
				<h2 class="m-samples__title">Drop it onto any page.</h2>
				<p class="m-samples__sub">
					Web apps, docs, portfolios — same script, same widget. No CMS, no rebuild.
				</p>
			</header>

			<SampleRow
				align="right"
				kicker="Web apps"
				title="Feedback inside the product."
				body="Pin comments to a row, a chart, or a button. Your team triages right where the issue happens — no separate bug tracker round-trip."
				highlights={[
					'Targets any element, not just text.',
					'Real-time presence across users.',
					'Hide for end users, show for staff.',
				]}
				render={<AppWindow />}
			/>

			<SampleRow
				align="left"
				kicker="Documentation"
				title="Comments on your docs."
				body="Readers leave inline notes on the exact sentence that tripped them up. Authors keep the thread next to the source — no more Slack archeology to find what a reviewer meant."
				highlights={[
					'Anchored to text, not page coordinates.',
					'Survives content edits and reflows.',
					'Resolves cleanly when the issue is fixed.',
				]}
				render={<DocsWindow />}
			/>

			<SampleRow
				align="right"
				kicker="Editorial"
				title="Even works on the pretty pages."
				body="Brand-safe styling — the widget themes itself to your palette. Stays out of the way until a reader actually wants to say something."
				highlights={[
					'Theme variables for full visual control.',
					'Bubbles hide on print and on share.',
					'Zero layout shift on host pages.',
				]}
				render={<EditorialWindow />}
			/>
		</section>
	);
}

interface SampleRowProps {
	align: 'left' | 'right';
	kicker: string;
	title: string;
	body: string;
	highlights: string[];
	render: preact.ComponentChildren;
}

function SampleRow(props: SampleRowProps) {
	const ref = useReveal<HTMLDivElement>();
	return (
		<div class={`m-samples__row m-samples__row--${props.align}`} ref={ref}>
			<div class="m-samples__copy">
				<span class="m-samples__row-kicker">{props.kicker}</span>
				<h3 class="m-samples__row-title">{props.title}</h3>
				<p class="m-samples__row-body">{props.body}</p>
				<ul class="m-samples__list">
					{props.highlights.map((h) => (
						<li key={h} class="m-samples__list-item">
							<span class="m-samples__list-dot" aria-hidden="true" />
							<span>{h}</span>
						</li>
					))}
				</ul>
			</div>
			<div class="m-samples__media">{props.render}</div>
		</div>
	);
}

/* ───────────────────────────────────────────────────────────────
   Three vastly different content samples. Each one styled in its
   own SCSS namespace so they don't share visual language. */

function DocsWindow() {
	return (
		<BrowserWindow url="docs.gridfin.io / api / queues" maxWidth="540px">
			<div class="m-sample m-sample--docs">
				<aside class="m-sample__toc">
					<div class="m-sample__toc-h">Reference</div>
					<a class="m-sample__toc-item">Queues</a>
					<a class="m-sample__toc-item m-sample__toc-item--on">Enqueue</a>
					<a class="m-sample__toc-item">Workers</a>
					<a class="m-sample__toc-item">Dead-letter</a>
				</aside>
				<article class="m-sample__article">
					<span class="m-sample__crumb">API · v2</span>
					<h4 class="m-sample__h">Enqueue a job</h4>
					<p class="m-sample__p">
						Push a single payload onto a named queue. Returns the assigned job
						id and the scheduled run time.
					</p>
					<p class="m-sample__p">
						<span class="m-sample__hl">
							Retries are exponential by default
						</span>{' '}
						— override with the <code>retry</code> option.
					</p>
					<div class="m-sample__code">
						<span>queue.enqueue(&#123;</span>
						<span class="m-sample__code-i">  job: 'send-email',</span>
						<span class="m-sample__code-i">  delay: 30,</span>
						<span>&#125;)</span>
					</div>
					<div class="m-sample__marker m-sample__marker--docs">
						<CommentMarker letter="N" hue={hueFor('Nora')} ariaLabel="Comment" />
					</div>
				</article>
			</div>
		</BrowserWindow>
	);
}

function AppWindow() {
	return (
		<BrowserWindow url="app.northbound / orders / today" maxWidth="540px">
			<div class="m-sample m-sample--app">
				<header class="m-sample__app-bar">
					<span class="m-sample__app-brand">northbound</span>
					<span class="m-sample__app-pill">live</span>
					<span class="m-sample__app-spacer" />
					<span class="m-sample__app-avatar" />
				</header>
				<div class="m-sample__app-body">
					<nav class="m-sample__app-nav">
						<a class="m-sample__app-nav-item m-sample__app-nav-item--on">
							Orders
						</a>
						<a class="m-sample__app-nav-item">Inventory</a>
						<a class="m-sample__app-nav-item">Routing</a>
						<a class="m-sample__app-nav-item">Reports</a>
					</nav>
					<div class="m-sample__app-main">
						<div class="m-sample__app-header">
							<h4 class="m-sample__app-h">Today's orders</h4>
							<span class="m-sample__app-count">14 open</span>
						</div>
						<div class="m-sample__app-row">
							<span class="m-sample__app-row-id">#4081</span>
							<span class="m-sample__app-row-name">Helmsworth, K.</span>
							<span class="m-sample__app-row-tag m-sample__app-row-tag--warn">
								awaiting
							</span>
						</div>
						<div class="m-sample__app-row m-sample__app-row--target">
							<span class="m-sample__app-row-id">#4082</span>
							<span class="m-sample__app-row-name">Tao, Lin</span>
							<span class="m-sample__app-row-tag m-sample__app-row-tag--ok">
								shipped
							</span>
							<div class="m-sample__marker m-sample__marker--app">
								<CommentMarker letter="S" hue={hueFor('Sam')} ariaLabel="Comment" />
							</div>
						</div>
						<div class="m-sample__app-row">
							<span class="m-sample__app-row-id">#4083</span>
							<span class="m-sample__app-row-name">Okada, R.</span>
							<span class="m-sample__app-row-tag">draft</span>
						</div>
					</div>
				</div>
			</div>
		</BrowserWindow>
	);
}

function EditorialWindow() {
	return (
		<BrowserWindow url="maisonatelier.fr" maxWidth="540px">
			<div class="m-sample m-sample--editorial">
				<div class="m-sample__ed-row">
					<span class="m-sample__ed-mark">M / A</span>
					<span class="m-sample__ed-meta">issue 04 · spring</span>
				</div>
				<h4 class="m-sample__ed-h">
					On the slow art <em>of mending</em>.
				</h4>
				<p class="m-sample__ed-byline">
					Photographs by C. Renoir &nbsp;·&nbsp; Words by J. Tate
				</p>
				<div class="m-sample__ed-art">
					<span class="m-sample__ed-grain" aria-hidden="true" />
					<span class="m-sample__ed-stripe" aria-hidden="true" />
				</div>
				<p class="m-sample__ed-p">
					An afternoon with the atelier's last hand-finisher, on linen,
					patience, and the quiet weight of doing one thing well.
				</p>
				<div class="m-sample__marker m-sample__marker--editorial">
					<CommentMarker letter="J" hue={hueFor('Jules')} ariaLabel="Comment" />
				</div>
			</div>
		</BrowserWindow>
	);
}
